/** [longitude, latitude] in degrees – the order deck.gl and MapLibre use. */
export type LngLat = [number, number];

export interface GeotabPoint {
  x: number;
  y: number;
}

const EARTH_RADIUS_M = 6_371_008.8;
const toRad = (deg: number) => (deg * Math.PI) / 180;
const toDeg = (rad: number) => (rad * 180) / Math.PI;

export function haversineM([lng1, lat1]: LngLat, [lng2, lat2]: LngLat): number {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
}

function destination([lng, lat]: LngLat, distanceM: number, bearingRad: number): LngLat {
  const delta = distanceM / EARTH_RADIUS_M;
  const phi1 = toRad(lat);
  const lambda1 = toRad(lng);
  const phi2 = Math.asin(
    Math.sin(phi1) * Math.cos(delta) + Math.cos(phi1) * Math.sin(delta) * Math.cos(bearingRad),
  );
  const lambda2 =
    lambda1 +
    Math.atan2(
      Math.sin(bearingRad) * Math.sin(delta) * Math.cos(phi1),
      Math.cos(delta) - Math.sin(phi1) * Math.sin(phi2),
    );
  return [toDeg(lambda2), toDeg(phi2)];
}

const samePoint = (a: LngLat, b: LngLat) => a[0] === b[0] && a[1] === b[1];

/** Closed ring of `segments + 1` points approximating a circle (Geotab stores zones as points only). */
export function circlePoints(center: LngLat, radiusM: number, segments = 48): LngLat[] {
  const ring: LngLat[] = [];
  for (let i = 0; i < segments; i++) ring.push(destination(center, radiusM, (2 * Math.PI * i) / segments));
  ring.push(ring[0]);
  return ring;
}

/** Closed, north-aligned square with side length `sideM` around `center`. */
export function squarePoints([lng, lat]: LngLat, sideM: number): LngLat[] {
  const half = sideM / 2;
  const dLat = toDeg(half / EARTH_RADIUS_M);
  const dLng = dLat / Math.cos(toRad(lat));
  const ring: LngLat[] = [
    [lng - dLng, lat + dLat],
    [lng + dLng, lat + dLat],
    [lng + dLng, lat - dLat],
    [lng - dLng, lat - dLat],
  ];
  ring.push(ring[0]);
  return ring;
}

/** Approximate area of a ring on the sphere, in square metres. */
export function polygonAreaM2(points: LngLat[]): number {
  if (points.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const [lng1, lat1] = points[i];
    const [lng2, lat2] = points[(i + 1) % points.length];
    sum += toRad(lng2 - lng1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
  }
  return Math.abs((sum * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2);
}

function toFiniteNumber(value: unknown): number | undefined {
  const number = typeof value === "string" ? Number(value) : value;
  return typeof number === "number" && Number.isFinite(number) ? number : undefined;
}

/** Reads Geotab `points` defensively: zones edited elsewhere may carry strings or broken entries. */
export function geotabPointsToLngLat(points: unknown[] | undefined): LngLat[] {
  const result: LngLat[] = [];
  for (const point of points ?? []) {
    if (!point || typeof point !== "object") continue;
    const x = toFiniteNumber((point as Record<string, unknown>).x);
    const y = toFiniteNumber((point as Record<string, unknown>).y);
    if (x !== undefined && y !== undefined) result.push([x, y]);
  }
  return result;
}

/**
 * Removes consecutive points closer than `toleranceM` (a double click adds the last point twice,
 * a jittered one a few pixels apart) and closes the ring.
 */
export function closePolygon(points: LngLat[], toleranceM = 0): LngLat[] {
  const ring: LngLat[] = [];
  for (const point of points) {
    const previous = ring[ring.length - 1];
    if (!previous || (!samePoint(point, previous) && haversineM(point, previous) > toleranceM)) ring.push(point);
  }
  if (ring.length > 1 && samePoint(ring[0], ring[ring.length - 1])) ring.pop();
  if (ring.length > 0) ring.push(ring[0]);
  return ring;
}

export function lngLatToGeotabPoints(points: LngLat[]): GeotabPoint[] {
  return closePolygon(points).map(([x, y]) => ({ x, y }));
}

/** Points of an open ring: the closing point is dropped so every vertex appears once. */
export function openRing(points: LngLat[]): LngLat[] {
  return points.length > 1 && samePoint(points[0], points[points.length - 1]) ? points.slice(0, -1) : points;
}

export function boundsOf(points: LngLat[]): [LngLat, LngLat] | null {
  if (points.length === 0) return null;
  let [minLng, minLat] = points[0];
  let [maxLng, maxLat] = points[0];
  for (const [lng, lat] of points) {
    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}
